import { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import Papa from 'papaparse'

interface MicrocuencaDetalle {
  _id?: string
  fid: number
  CODIGO: string
  Nombre_De_Microcuenca: string
  Municipio: string
  Superficie_agricola?: number
  Superficie_Microcuenca?: number
  Superficie_pasto?: number
  Superficie_forestal?: number
  Densidad_de_poblacion?: number
  Poblacion_total?: number
  Indice_de_marginacion?: number
  Grado_de_indice_de_marginacion?: string
  Indice_de_rezago?: number
  Clase_de_rezago?: string
  Precipitacion?: number
  Temperatura_media?: number
  Altitud?: number
  Pendiente?: number
  Superficie_deforestada?: number
  Erosion?: number
  Categoria_de_erosion?: string
  Nivel_de_erosion?: number
  Ponderacion_de_escurrimiento?: number
  Hectareas_en_ANP?: number
  createdAt?: string
  updatedAt?: string
  creator?: string
}

interface CSVRowDetalle {
  fid: string
  CODIGO: string
  MUNICIPIO: string
  MICROCUENCA: string
  'Superficie Agricola': string
  'Sup_Micro_': string
  'Superficie Pasto': string
  'Superficie Forestal': string
  'Densidad de población': string
  'Precipitación': string
  'Temperatura': string
  'Altitud': string
  'Pendiente': string
  [key: string]: string // Para campos adicionales
}

export const useMicrocuencasDetalleCRUD = () => {
  const [microcuencasDetalle, setMicrocuencasDetalle] = useState<MicrocuencaDetalle[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  const fetchMicrocuencasDetalle = useCallback(async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.microcuencas_detalle.list({
        sort: { createdAt: -1 }
      })
      setMicrocuencasDetalle(response.list || [])
    } catch (error: any) {
      console.error('Error al cargar microcuencas detalle:', error)
      toast.error('Error al cargar los datos detallados')
    } finally {
      setLoading(false)
    }
  }, [])

  const createMicrocuencaDetalle = async (data: Omit<MicrocuencaDetalle, '_id'>) => {
    try {
      const newRecord = await lumi.entities.microcuencas_detalle.create({
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: 'user'
      })
      setMicrocuencasDetalle(prev => [newRecord, ...prev])
      toast.success('Registro detallado creado exitosamente')
      return newRecord
    } catch (error: any) {
      console.error('Error al crear registro detallado:', error)
      toast.error('Error al crear el registro detallado')
      throw error
    }
  }

  const updateMicrocuencaDetalle = async (id: string, updates: Partial<MicrocuencaDetalle>) => {
    try {
      const updatedRecord = await lumi.entities.microcuencas_detalle.update(id, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      setMicrocuencasDetalle(prev => prev.map(m => m._id === id ? updatedRecord : m))
      toast.success('Registro detallado actualizado exitosamente')
      return updatedRecord
    } catch (error: any) {
      console.error('Error al actualizar registro detallado:', error)
      toast.error('Error al actualizar el registro detallado')
      throw error
    }
  }

  const deleteMicrocuencaDetalle = async (id: string) => {
    try {
      await lumi.entities.microcuencas_detalle.delete(id)
      setMicrocuencasDetalle(prev => prev.filter(m => m._id !== id))
      toast.success('Registro detallado eliminado exitosamente')
    } catch (error: any) {
      console.error('Error al eliminar registro detallado:', error)
      toast.error('Error al eliminar el registro detallado')
      throw error
    }
  }

  const processCSVData = (csvData: CSVRowDetalle[]): Omit<MicrocuencaDetalle, '_id'>[] => {
    return csvData.map(row => ({
      fid: parseInt(row.fid) || 0,
      CODIGO: row.CODIGO || '',
      Nombre_De_Microcuenca: row.MICROCUENCA || '',
      Municipio: row.MUNICIPIO || '',
      Superficie_agricola: parseFloat(row['Superficie Agricola']) || 0,
      Superficie_Microcuenca: parseFloat(row['Sup_Micro_']) || 0,
      Superficie_pasto: parseFloat(row['Superficie Pasto']) || 0,
      Superficie_forestal: parseFloat(row['Superficie Forestal']) || 0,
      Densidad_de_poblacion: parseFloat(row['Densidad de población']) || 0,
      Precipitacion: parseFloat(row['Precipitación']) || 0,
      Temperatura_media: parseFloat(row['Temperatura']) || 0,
      Altitud: parseFloat(row['Altitud']) || 0,
      Pendiente: parseFloat(row['Pendiente']) || 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      creator: 'csv_import'
    }))
  }

  const uploadCSVFromURL = async (url: string) => {
    setUploading(true)
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error('Error al descargar el archivo')
      }
      const csvText = await response.text()
      
      return new Promise<void>((resolve, reject) => {
        Papa.parse<CSVRowDetalle>(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: async (results) => {
            try {
              if (results.errors.length > 0) {
                console.warn('Errores en el CSV:', results.errors)
              }

              const processedData = processCSVData(results.data)
              
              if (processedData.length === 0) {
                toast.error('No se encontraron datos válidos en el archivo')
                reject(new Error('No valid data found'))
                return
              }

              const batchSize = 10
              let successCount = 0
              
              for (let i = 0; i < processedData.length; i += batchSize) {
                const batch = processedData.slice(i, i + batchSize)
                
                try {
                  const promises = batch.map(data => 
                    lumi.entities.microcuencas_detalle.create(data)
                  )
                  await Promise.all(promises)
                  successCount += batch.length
                } catch (batchError) {
                  console.error(`Error en lote ${i / batchSize + 1}:`, batchError)
                }
              }

              if (successCount > 0) {
                toast.success(`${successCount} registros detallados importados exitosamente`)
                await fetchMicrocuencasDetalle()
              } else {
                toast.error('No se pudieron importar los registros')
              }

              resolve()
            } catch (error) {
              console.error('Error procesando CSV:', error)
              toast.error('Error al procesar el archivo CSV')
              reject(error)
            }
          },
          error: (error) => {
            console.error('Error parseando CSV:', error)
            toast.error('Error al leer el archivo CSV')
            reject(error)
          }
        })
      })
    } catch (error) {
      console.error('Error en carga de CSV desde URL:', error)
      toast.error('Error al cargar el archivo desde la URL')
      throw error
    } finally {
      setUploading(false)
    }
  }

  const clearAllData = async () => {
    try {
      const ids = microcuencasDetalle.map(m => m._id!).filter(Boolean)
      if (ids.length > 0) {
        const batchSize = 20
        let deletedCount = 0
        
        for (let i = 0; i < ids.length; i += batchSize) {
          const batch = ids.slice(i, i + batchSize)
          try {
            await lumi.entities.microcuencas_detalle.deleteMany(batch)
            deletedCount += batch.length
          } catch (batchError) {
            console.error(`Error eliminando lote ${i / batchSize + 1}:`, batchError)
          }
        }
        
        setMicrocuencasDetalle([])
        toast.success(`${deletedCount} registros detallados eliminados exitosamente`)
      }
    } catch (error: any) {
      console.error('Error al limpiar datos detallados:', error)
      toast.error('Error al limpiar los datos detallados')
      throw error
    }
  }

  const removeDuplicates = async () => {
    try {
      const codigoMap = new Map<string, MicrocuencaDetalle[]>()
      
      microcuencasDetalle.forEach(m => {
        if (!codigoMap.has(m.CODIGO)) {
          codigoMap.set(m.CODIGO, [])
        }
        codigoMap.get(m.CODIGO)!.push(m)
      })
      
      const idsToDelete: string[] = []
      codigoMap.forEach((items, codigo) => {
        if (items.length > 1) {
          const sorted = items.sort((a, b) => 
            new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
          )
          idsToDelete.push(...sorted.slice(1).map(item => item._id!).filter(Boolean))
        }
      })
      
      if (idsToDelete.length > 0) {
        const batchSize = 50
        let deletedCount = 0
        
        for (let i = 0; i < idsToDelete.length; i += batchSize) {
          const batch = idsToDelete.slice(i, i + batchSize)
          try {
            await lumi.entities.microcuencas_detalle.deleteMany(batch)
            deletedCount += batch.length
          } catch (batchError) {
            console.error(`Error eliminando lote ${i / batchSize + 1}:`, batchError)
          }
        }
        
        setMicrocuencasDetalle(prev => prev.filter(m => !idsToDelete.includes(m._id!)))
        toast.success(`${deletedCount} registros duplicados eliminados`)
      } else {
        toast.success('No se encontraron duplicados')
      }
    } catch (error: any) {
      console.error('Error al eliminar duplicados:', error)
      toast.error('Error al eliminar duplicados')
      throw error
    }
  }

  useEffect(() => {
    fetchMicrocuencasDetalle()
  }, [fetchMicrocuencasDetalle])

  return {
    microcuencasDetalle,
    loading,
    uploading,
    fetchMicrocuencasDetalle,
    createMicrocuencaDetalle,
    updateMicrocuencaDetalle,
    deleteMicrocuencaDetalle,
    uploadCSVFromURL,
    clearAllData,
    removeDuplicates
  }
}
